<template>
    <div class="sub-assignment-nav-item">
        <div
            class="sub-assignment-nav-header"
            :class="[{ active: selected }]"
            role="tab"
            @click="selectSubAssignment(subAssignmentIn.id)">
            <b-button
                class="btn-nav-header"
                v-b-toggle="`collapse-${subAssignmentIn.id}`"
                variant="primary"
                block
                :disabled="selected">
                <div class="title-wrapper">
                    <i class="fas fa-fw fa-arrows-alt-v sub-assignment-drag-handle"> </i>
                    <div class="title">
                        <span>{{ subAssignmentIn.name }}</span>
                        <i class="fas fa-fw fa-chevron-down state-icon"></i>
                    </div>
                </div>
            </b-button>
        </div>

        <!-- invisible list to make drop work when the original list is collapsed and therefor not usable-->
        <draggable
            class="invisible-drop"
            v-if="!selected"
            :list="pages"
            ghost-class="ghost"
            group="pages"
            @change="orderChanged">
        </draggable>

        <b-collapse
            class="sub-assignment-pages-collapse"
            :id="`collapse-${subAssignmentIn.id}`"
            accordion="my-accordion"
            role="tabpanel"
            :visible="selected">
            <b-list-group
                class="sub-assignment-pages-group"
                :id="`collapse-${subAssignmentIn.id}`">
                <draggable
                    :list="pages"
                    group="pages"
                    handle=".sub-assignment-drag-handle"
                    ghost-class="ghost"
                    @change="orderChanged">
                    <b-list-group-item
                        class="sub-assignment-page"
                        v-for="page in pages"
                        :key="page.id">
                        <span><i class="fas fa-fw fa-arrows-alt-v sub-assignment-drag-handle"></i></span>
                        <span>
                            <a
                                class="page-anchor-color"
                                :href="'#page-' + page.id">
                                <div v-if="page.name">
                                    {{ page.name }}
                                </div>

                                <div v-else>{{ $t('common.page') + ' ' + page.order }}</div>
                            </a>
                        </span>
                    </b-list-group-item>
                </draggable>
            </b-list-group>
        </b-collapse>
    </div>
</template>

<script>
import draggable from 'vuedraggable';

export default {
    name: 'design-2021-lesson-module-assignment-sub-assignment-pages-nav-component',
    components: {
        draggable,
    },
    props: ['subAssignmentIn', 'selected'],
    data() {
        return {
            pages: this.subAssignmentIn.pages.slice(), // draggable is mutating the array
        };
    },
    watch: {
        subAssignmentIn: function (subAssignment) {
            this.pages = subAssignment.pages.slice(); // draggable is mutating the array
        },
    },
    methods: {
        selectSubAssignment(id) {
            const name = 'assignment-edit-selected-sub-assignment-' + this.subAssignmentIn.assignment_id;
            localStorage.setItem(name, id);
            this.$emit('selected-sub-assignment', id);
        },
        orderChanged(target) {
            let x = 1;
            let blocks = [];

            for (const page of this.pages) {
                blocks.push({
                    id: page.id,
                    order: x,
                });
                x++;
            }

            const data = {
                sub_assignment_id: this.subAssignmentIn.id,
                blocks: blocks,
            };
            this.axios.post(this.routes.assignmentPageOrder(), data).then(
                (response) => {
                    this.$emit('sub-assignment-updated', response.data);
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
    },
};
</script>

<style scoped lang="scss">
.ghost {
    background-color: darken($secondary-background-color, 10%) !important;
}

.sub-assignment-nav-item {
    background-color: $white;
    border-radius: $border-radius-content;

    .sub-assignment-nav-header {
        .title-wrapper {
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            flex: 1;

            .title {
                display: flex;
                flex: 1 1 calc(100% - 16px);
                flex-direction: row;
                flex-wrap: wrap;
                justify-content: space-between;
                text-align: left;

                span {
                    display: flex;
                    flex: 1 1 calc(100% - 26px);
                }
            }
        }

        .btn-nav-header {
            background-color: $white;
            border: 1px solid $white;
            color: $text-color;

            .sub-assignment-drag-handle {
                color: $text-color;
                margin-right: map-get($grid-gutter-spacing, 'eight');

                &:hover {
                    cursor: pointer;
                }
            }

            .state-icon {
                transform-origin: center;
                transition: all 3ms ease;
                align-self: center;
            }

            &:hover {
                color: $white;
                background-color: $info;

                &,
                .sub-assignment-drag-handle {
                    color: $white;
                }
            }

            &.not-collapsed {
                .state-icon {
                    transform: rotate(-180deg);
                }
            }

            &.disabled {
                opacity: 1;
            }
        }

        .btn-nav-header,
        &.active .btn-nav-header {
            outline: none;
            box-shadow: none;
            outline: none;
            border-radius: $border-radius-content;
            justify-content: space-between;
            align-items: center;
        }
    }

    .sub-assignment-pages-group > div {
        .sub-assignment-page {
            display: flex;
            flex-direction: row;
            align-items: center;
            background-color: transparent;
            border: none;
            padding-left: map-get($grid-gutter-spacing, 'forty-two');

            .sub-assignment-drag-handle {
                color: $text-color;
                margin-right: map-get($grid-gutter-spacing, 'eight');
            }

            .page-anchor-color {
                color: $text-color;
            }

            &:not(:last-child) {
                border-bottom: 3px dotted $border-color;
            }

            &:last-child {
                border-bottom: 3px dotted transparent;
            }

            &:hover {
                cursor: pointer;

                a {
                    color: $primary;
                }
            }
        }
    }

    &:not(:last-child) {
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
    }
}

@media (min-width: map-get($grid-breakpoints, 'xl')) {
    .sub-assignment-nav-item {
        .sub-assignment-nav-header {
            .title-wrapper {
                .title {
                    flex-wrap: nowrap;
                }
            }
        }
    }
}
</style>
